import { createClient, SupabaseClient, SupabaseClientOptions } from '@supabase/supabase-js';
import type { HomepageSettings } from '@/types/api';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;
if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

// Supabase client configuration with timeouts and retry logic
const supabaseConfig: SupabaseClientOptions<'public'> = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: {
      'x-client-info': 'gitta-portfolio',
    },
    // Fetch configuration with timeout
    fetch: (url: RequestInfo | URL, options: RequestInit = {}) => {
      // Set timeout to 8 seconds (before Vercel's 10s limit)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      return fetch(url, {
        ...options,
        signal: controller.signal,
      }).finally(() => clearTimeout(timeoutId));
    },
  },
  // Realtime configuration
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
};

// Client-side Supabase client (with RLS)
export const supabase = createClient<any, 'public', any>(supabaseUrl, supabaseKey, supabaseConfig);

// Server-side Supabase client (bypasses RLS) - only available on server
export const supabaseAdmin: SupabaseClient<any, 'public', any> | null = (() => {
  // Check if we're on the server side
  if (typeof window === 'undefined') {
    const supabaseServiceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;

    if (!supabaseServiceKey) {
      console.warn('SUPABASE_SERVICE_ROLE_KEY not found - admin operations will not work');
      return null;
    }

    return createClient<any, 'public', any>(supabaseUrl, supabaseServiceKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
      },
      db: {
        schema: 'public',
      },
      global: {
        headers: {
          'x-client-info': 'gitta-portfolio-admin',
        },
        // Admin client also needs timeout protection
        fetch: (url: RequestInfo | URL, options: RequestInit = {}) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000);

          return fetch(url, {
            ...options,
            signal: controller.signal,
          }).finally(() => clearTimeout(timeoutId));
        },
      },
    });
  }

  // Return null on client side
  return null;
})();

// Database types
export interface HomepageSettingsRecord {
  id: number;
  settings: HomepageSettings; // Properly typed JSON field
  created_at: string;
  updated_at: string;
}

export interface BlogPostRecord {
  id: number;
  slug: string;
  title: string;
  description?: string;
  content: string;
  category: string;
  author: string;
  tags: string[];
  thumbnail_image?: string;
  hover_image?: string;
  created_at: string;
  updated_at: string;
}